import { ListResponse, PaginationRequest, Response } from './client/types'
import { useAPI } from './client/useAPI'

export type CompanySearchItem = {
  id: string
  name: string
}

export type CompanyListItem = {
  id: string
  name: string
  manager: string
  inn: string
  kpp: string
  ogrn: string
  taxForm: string
  businessAddress: string
  postalAddress: string
  bic: string
  account: string
}

export type CompaniesRequest = PaginationRequest & {
  search?: string
}

type CompaniesResponse = ListResponse<CompanyListItem>

export const useCompanies = () => {
  return useAPI<CompaniesResponse, CompaniesRequest>('GET', '/companies', {
    withToken: true,
  })
}

export type Signer = {
  firstName: string
  lastName: string
  middleName?: string
  position: string
}

export type Commission = {
  operation: 'orders'
  type: 'fixed' | 'relative'
  amount: string
}

export type CompanyData = {
  id: string
  name: string
  manager: string
  inn: string
  kpp: string
  ogrn: string
  taxForm: string
  businessAddress: string
  postalAddress: string
  bic: string
  account: string
  ordersEnabled: boolean
  signer: Signer
  commission: Commission
  createdAt: string
}

type CompanyResponse = Response<CompanyData>
type CompanyRequest = CompanyData

export const useCompany = (id: string) => {
  return useAPI<CompanyResponse>('GET', `/companies/${id}`, {
    withToken: true,
  })
}

export const useSaveCompany = (id: string) => {
  return useAPI<CompanyResponse, CompanyRequest>('PATCH', `/companies/${id}`, {
    withToken: true,
  })
}

export type CompanyBalanceData = {
  amount: number
  currency: string
}

export type CompanyBalanceResponse = Response<CompanyBalanceData>

export const useCompanyBalance = (id: string) => {
  return useAPI<CompanyBalanceResponse>('GET', `/companies/${id}/balance`, {
    withToken: true,
  })
}

export type CompanyAdjustBalanceData = {
  amount: string
  comment: string
}

type CompanyAdjustBalanceResponse = Response<undefined>
type CompanyAdjustBalanceRequest = CompanyAdjustBalanceData

export const useCompanyBalanceAdjust = (id: string) => {
  return useAPI<CompanyAdjustBalanceResponse, CompanyAdjustBalanceRequest>(
    'POST',
    `/companies/${id}/balance/adjust`,
    {
      withToken: true,
    }
  )
}

export type CompanyCredit = {
  expiryDate: string
  amount: string
  createdAt: string
}

export type CompanyCreditResponse = Response<CompanyCredit>

export const useCompanyCredit = (id: string) => {
  return useAPI<CompanyCreditResponse>('GET', `/companies/${id}/credit`, {
    withToken: true,
  })
}

export type CompanyAddCredit = {
  amount: string
  expiredAt: string
  comment: string
}

type CompanyAddCreditResponse = Response<CompanyCredit>
type CompanyAddCreditRequest = CompanyAddCredit

export const useCompanyAddCredit = (id: string) => {
  return useAPI<CompanyAddCreditResponse, CompanyAddCreditRequest>(
    'POST',
    `/companies/${id}/credit`,
    {
      withToken: true,
    }
  )
}

export type CompanyUpdateCredit = CompanyAddCredit
type CompanyUpdateCreditResponse = Response<CompanyCredit>
type CompanyUpdateCreditRequest = CompanyUpdateCredit

export const useCompanyUpdateCredit = (id: string) => {
  return useAPI<CompanyUpdateCreditResponse, CompanyUpdateCreditRequest>(
    'PATCH',
    `/companies/${id}/credit`,
    {
      withToken: true,
    }
  )
}

export type CompanyUserData = {
  id: string
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
}

export type CompanyUsersRequest = PaginationRequest

type CompanyUsersResponse = ListResponse<CompanyUserData>

export const useCompanyUsers = (id: string) => {
  return useAPI<CompanyUsersResponse, CompanyUsersRequest>(
    'GET',
    `/companies/${id}/users`,
    {
      withToken: true,
    }
  )
}
